import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { SUCCESS_METRICS } from '../../../constants/contentConfig';
import { useBooking } from '../../../contexts/BookingContext';
import { useGoogleAnalytics } from '../../../hooks/useGoogleAnalytics';

/**
 * OptimizedHeroSection - Enhanced hero with A/B testing and improved UX
 * 
 * Key improvements:
 * - A/B testing framework for different hero variations
 * - Simplified, scannable content hierarchy
 * - Enhanced trust signals and social proof
 * - Better mobile responsiveness
 * - Improved CTA hierarchy
 * - Performance optimizations
 * - Smooth fade-in animations on load
 * - Parallax scrolling effects
 * - Enhanced micro-interactions
 */
const highlightPoints = [
  {
    icon: 'Rocket',
    title: 'GCC Setup Launch Blueprint',
    description: '90-day program with transparent pricing, regulatory readiness, and milestone SLAs.'
  },
  {
    icon: 'Globe2',
    title: 'GCC Expansion Playbooks',
    description: 'Dual-city scale, incentive negotiations, and executive governance cadences.'
  },
  {
    icon: 'UserCheck',
    title: 'Executive Hiring & TA Strategy',
    description: 'Leadership search and TA workshops with a 25-day SkillStack™ talent promise.'
  },
  {
    icon: 'Layers',
    title: 'SkillStack™ Staffing Engine',
    description: 'Staffing models with candidate concierge, retention analytics, and zero-risk clauses.'
  }
];

const OptimizedHeroSection = () => {
  // Default to true so prerendered HTML and initial client render match
  // for hydration. The 50ms fade-in only matters in dev/non-prerendered
  // contexts; the prerendered build already shows the content fully visible.
  const [isLoaded, setIsLoaded] = useState(true);
  const { openBooking } = useBooking();
  const { trackEvent } = useGoogleAnalytics();

  useEffect(() => {
    if (!isLoaded) {
      const timeout = setTimeout(() => setIsLoaded(true), 50);
      return () => clearTimeout(timeout);
    }
  }, [isLoaded]);

  const handleSecondaryClick = () => {
    trackEvent('hero_secondary_cta', {
      destination: '/roi-calculator-hub-interactive-decision-tools'
    });
  };

  return (
    <section className="relative overflow-hidden bg-background" data-testid="hero-section">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute inset-y-0 right-0 w-1/2 bg-grid-pattern opacity-[0.03]" />
      </div>

      <div className="container-executive relative z-10 pt-24 pb-20 lg:pt-32 lg:pb-28">
        <div className={`flex flex-col-reverse lg:flex-row lg:items-center gap-16 xl:gap-20 transition-opacity duration-700 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}>
          {/* Left column: Messaging */}
          <div className="flex-1">
            <div className="inline-flex items-center space-x-2 rounded-full border border-accent/20 bg-accent/10 px-4 py-2 text-sm font-medium text-accent shadow-sm">
              <div className="h-2 w-2 animate-pulse rounded-full bg-accent" />
              <span>End-to-End GCC Launch • 90-Day Guarantee</span>
            </div>

            <h1 className="mt-6 text-4xl font-bold leading-tight text-text-primary sm:text-5xl lg:text-6xl xl:text-[4.25rem]">
              Service-First GCC Launch, Expansion & Staffing
            </h1>

            <p className="mt-6 max-w-2xl text-lg text-text-secondary">
              Bootminds Elite orchestrates Launch Blueprint setup, multi-city expansion, executive hiring, and SkillStack™ staffing so you stand up India capability centers with transparent economics and zero-risk engagement.
            </p>

            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {highlightPoints.map(({ icon, title, description }) => (
                <li key={title} className="flex items-start space-x-3 rounded-xl border border-border/60 bg-card/70 p-4 shadow-sm backdrop-blur">
                  <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Icon name={icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{title}</p>
                    <p className="text-sm text-text-secondary">{description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button
                size="xl"
                className="w-full sm:w-auto bg-primary text-primary-foreground px-8 py-4 text-lg font-semibold shadow-lg shadow-primary/30 transition-all duration-200 hover:translate-y-[-1px] hover:shadow-xl" 
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
                onClick={() => openBooking('hero_primary_cta')}
              >
                Book Strategy Call
              </Button>

              <Button
                variant="outline"
                size="xl"
                className="w-full sm:w-auto border-primary/40 text-primary hover:bg-primary/10 px-8 py-4 text-lg font-semibold"
                asChild
              >
                <Link to="/calculator" onClick={handleSecondaryClick}>
                  <Icon name="Calculator" size={18} className="mr-2" />
                  View ROI Calculators
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="xl"
                className="w-full sm:w-auto text-primary hover:text-primary"
                asChild
              >
                <Link to="/about">
                  <Icon name="Users" size={18} className="mr-2" />
                  Meet the Leadership Team
                </Link>
              </Button>
            </div>
          </div>

          {/* Right column: Visual proof */}
          <div className="flex-1">
            <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-card/80 shadow-2xl backdrop-blur">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
              <div className="relative p-8 lg:p-10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-text-secondary">Execution Dashboard</p>
                    <h2 className="mt-2 text-2xl font-semibold text-text-primary">India GCC Launch Control</h2>
                  </div>
                  <Icon name="Globe" size={32} className="text-accent" />
                </div>

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {SUCCESS_METRICS.slice(0, 4).map((metric) => (
                    <div key={metric.label} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium uppercase tracking-wide text-text-secondary">{metric.label}</span>
                        <Icon name={metric.icon || 'TrendingUp'} size={18} className="text-accent" />
                      </div>
                      <p className="mt-3 text-3xl font-bold text-text-primary">{metric.value}</p>
                      <p className="text-xs font-medium text-success mt-1">{metric.accent}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 rounded-xl border border-success/30 bg-success/10 p-5 text-sm text-text-primary font-medium">
                  <span className="text-success">Global enterprises</span> trust Bootminds for GCC launches and operating turnarounds — with 1,000+ professionals placed across our team's GCC engagements since 2018, with $20M+ in client savings delivered during the 2018-2024 Intelliswift alliance.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OptimizedHeroSection;
